import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogMain,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type MultiSelectOption,
} from '@ncoder/ui';
import type { OrgRole, OrgServiceCatalogItem } from '@/services/org-admin';

interface RoleFormValues {
  name: string;
}

export interface RoleFormSubmitData {
  name: string;
  // Omitted when editing the seeded Owner role (never changeable) or when left at the default.
  management_level?: 'admin' | 'member';
  capability_keys: string[];
}

export interface RoleFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialRole: OrgRole | null;
  serviceCatalog: OrgServiceCatalogItem[];
  // Only an owner may grant the admin management level to a role.
  canGrantAdmin: boolean;
  isSubmitting: boolean;
  onSubmit: (data: RoleFormSubmitData) => void;
  onClose: () => void;
}

export function RoleFormModal({
  open,
  mode,
  initialRole,
  serviceCatalog,
  canGrantAdmin,
  isSubmitting,
  onSubmit,
  onClose,
}: RoleFormModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<RoleFormValues>({ defaultValues: { name: '' }, mode: 'onChange' });

  const [selectedServiceSlugs, setSelectedServiceSlugs] = useState<string[]>(
    initialRole?.capabilities.map((capability) => capability.key) ?? [],
  );

  // The seeded Owner role's management_level can never be changed here (there's
  // exactly one, and "owner" is never a settable value via this form).
  const isOwnerRole = initialRole?.management_level === 'owner';
  const [managementLevel, setManagementLevel] = useState<'admin' | 'member'>(
    initialRole?.management_level === 'admin' ? 'admin' : 'member',
  );

  useEffect(() => {
    if (!open) return;
    reset({ name: initialRole?.name ?? '' });
    setSelectedServiceSlugs(initialRole?.capabilities.map((capability) => capability.key) ?? []);
    setManagementLevel(initialRole?.management_level === 'admin' ? 'admin' : 'member');
  }, [open, initialRole, reset]);

  const serviceOptions: MultiSelectOption[] = serviceCatalog.map((service) => ({
    label: service.label,
    value: service.key,
  }));

  const handleCancel = () => onClose();

  const submit = handleSubmit((data) => {
    onSubmit({
      name: data.name,
      management_level: isOwnerRole ? undefined : managementLevel,
      capability_keys: selectedServiceSlugs,
    });
  });

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            {mode === 'edit' ? 'Edit role' : 'Create role'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Name the role and pick which services it grants access to.
          </DialogDescription>
        </DialogHeader>

        <DialogMain className="space-y-3">
          <div className="space-y-1.5">
            <label htmlFor="org-role-name" className="text-xs font-medium text-foreground">
              Name
            </label>
            <input
              id="org-role-name"
              placeholder="e.g. Analyst"
              {...register('name', { required: 'Name is required' })}
              disabled={isSubmitting}
              className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Management level</label>
            {isOwnerRole ? (
              <p className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                Owner — every org has exactly one Owner role; its level can't be changed.
              </p>
            ) : (
              <Select
                value={managementLevel}
                onValueChange={(value) => setManagementLevel(value as 'admin' | 'member')}
                disabled={isSubmitting}
              >
                <SelectTrigger className="text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  <SelectItem value="member" className="text-xs">
                    Member — no admin authority, only granted services
                  </SelectItem>
                  {canGrantAdmin && (
                    <SelectItem value="admin" className="text-xs">
                      Admin — can invite members and assign member-level roles
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">
              Services <span className="text-destructive">*</span>
            </label>
            <MultiSelect
              key={initialRole?.id ?? 'create'}
              options={serviceOptions}
              onValueChange={setSelectedServiceSlugs}
              defaultValue={initialRole?.capabilities.map((capability) => capability.key) ?? []}
              placeholder="Select services…"
              searchable
              modalPopover
            />
            {selectedServiceSlugs.length === 0 && (
              <p className="text-xs text-destructive">Select at least one service.</p>
            )}
          </div>
        </DialogMain>

        <DialogFooter>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
            className="rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!isValid || selectedServiceSlugs.length === 0 || isSubmitting}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
          >
            {isSubmitting && <Loader2 className="h-3 w-3 animate-spin" />}
            {isSubmitting ? 'Saving…' : mode === 'edit' ? 'Save role' : 'Create role'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
